import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import edgeFunctionService from '../../services/edgeFunctionService';
import {
  buildFathomTranscriptObject,
  getExternalDomains,
  getMeetingDuration,
} from './transcriptUtils';

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * Fathom meeting import for a project: integration check, week-by-week
 * meeting listing, selection, domain-conflict confirmation, import with
 * progress, and the post-import review of unlinked attendee domains.
 *
 * @param {object} params
 * @param {object} params.authState - EnterpriseAuth state (user, activeAccount).
 * @param {Function} [params.onImportFathomSessions] - async (meetingData) => result; creates one session.
 * @param {object|null} [params.projectCustomer] - Customer of the project (with `domain` array).
 * @param {Function} [params.findCustomerByDomain] - (domain) => customer|null.
 * @param {Function} [params.onUpdateCustomer] - async (customerId, updates) => result.
 * @returns {object} State and handlers for the Fathom dialogs.
 */
export default function useFathomImport({
  authState,
  onImportFathomSessions,
  projectCustomer,
  findCustomerByDomain,
  onUpdateCustomer,
}) {
  const userId = authState.user?.id;
  const accountId = authState.activeAccount?.id;

  const [hasIntegration, setHasIntegration] = useState(null); // null = unknown
  const [open, setOpen] = useState(false);
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState({});
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0, currentName: '' });
  const [weeksBack, setWeeksBack] = useState(1);

  // Pre-import domain conflict confirmation
  const [conflictOpen, setConflictOpen] = useState(false);
  const [conflicts, setConflicts] = useState([]); // [{domain, matchedCustomer}]
  const [pendingMeetings, setPendingMeetings] = useState([]);

  // Post-import domain review
  const [domainReviewOpen, setDomainReviewOpen] = useState(false);
  const [domainReviewData, setDomainReviewData] = useState([]); // [{domain, status, matchedCustomer, selected}]
  const [domainReviewSaving, setDomainReviewSaving] = useState(false);

  /** Whether the user has a Fathom API key; controls the "Import from Fathom" entry point. */
  useEffect(() => {
    if (!userId || !accountId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const { data, error: queryError } = await supabase
          .from('integrations')
          .select('fathom_api_key')
          .eq('user_id', userId)
          .eq('account_id', accountId)
          .maybeSingle();
        if (cancelled) return;
        if (queryError) {
          console.warn('Error checking Fathom integration:', queryError);
          setHasIntegration(false);
          return;
        }
        setHasIntegration(Boolean(data?.fathom_api_key));
      } catch (err) {
        console.warn('Error checking Fathom integration:', err);
        if (!cancelled) setHasIntegration(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId, accountId]);

  /**
   * Fetch all meetings in a date window, following cursors until done.
   * @param {string} createdAfter - ISO start of the window.
   * @param {string} createdBefore - ISO end of the window.
   * @param {boolean} [append=false] - Append to the current list instead of replacing it.
   */
  const fetchMeetings = useCallback(async (createdAfter, createdBefore, append = false) => {
    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
      setMeetings([]);
    }
    setError('');

    try {
      let allMeetings = [];
      let cursor = null;
      let hasMore = true;

      while (hasMore) {
        const response = await edgeFunctionService.callFunction('fathom-api', {
          action: 'list-meetings',
          userId,
          accountId,
          createdAfter,
          createdBefore,
          cursor,
        });

        if (!response?.success) {
          throw new Error(response?.error || 'Failed to fetch meetings');
        }

        const page = response.meetings || [];
        allMeetings = [...allMeetings, ...page];
        setHasIntegration(true);

        if (response.nextCursor) {
          cursor = response.nextCursor;
          // Show progress while later pages load.
          if (append) setMeetings((prev) => [...prev, ...page]);
          else setMeetings([...allMeetings]);
        } else {
          hasMore = false;
        }
      }

      if (append) {
        setMeetings((prev) => {
          // Deduplicate by recording_id in case of overlap
          const existingIds = new Set(prev.map((m) => m.recording_id));
          return [...prev, ...allMeetings.filter((m) => !existingIds.has(m.recording_id))];
        });
      } else {
        setMeetings(allMeetings);
      }
    } catch (err) {
      setError(err.message || 'Failed to load Fathom meetings');
      if (err.message?.includes('No Fathom API key')) {
        setHasIntegration(false);
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [userId, accountId]);

  /** Open the import dialog with the past week's meetings. */
  const openImport = useCallback(() => {
    setOpen(true);
    setSelected({});
    setWeeksBack(1);
    const now = Date.now();
    fetchMeetings(new Date(now - WEEK_MS).toISOString(), new Date(now).toISOString());
  }, [fetchMeetings]);

  const closeImport = useCallback(() => {
    if (!importing) setOpen(false);
  }, [importing]);

  /** Load one more week of older meetings and append them. */
  const loadOlder = useCallback(() => {
    const nextWeeks = weeksBack + 1;
    const now = Date.now();
    setWeeksBack(nextWeeks);
    fetchMeetings(
      new Date(now - nextWeeks * WEEK_MS).toISOString(),
      new Date(now - weeksBack * WEEK_MS).toISOString(),
      true
    );
  }, [weeksBack, fetchMeetings]);

  const toggleSelected = useCallback((recordingId) => {
    setSelected((prev) => ({ ...prev, [recordingId]: !prev[recordingId] }));
  }, []);

  const allSelected = meetings.length > 0 && meetings.every((m) => selected[m.recording_id]);

  const toggleSelectAll = useCallback(() => {
    if (allSelected) {
      setSelected({});
    } else {
      const next = {};
      meetings.forEach((m) => { next[m.recording_id] = true; });
      setSelected(next);
    }
  }, [allSelected, meetings]);

  const selectedCount = Object.values(selected).filter(Boolean).length;

  /**
   * Import meetings one by one (after optional confirmation).
   * @param {object[]} meetingsToImport - Meetings.
   */
  const executeImport = useCallback(async (meetingsToImport) => {
    if (!meetingsToImport || meetingsToImport.length === 0) return;

    setConflictOpen(false);
    setPendingMeetings([]);
    setConflicts([]);
    setImporting(true);
    setError('');
    setProgress({ current: 0, total: meetingsToImport.length, currentName: '' });

    const results = { success: 0, failed: 0, errors: [], allExternalDomains: [] };

    for (let i = 0; i < meetingsToImport.length; i++) {
      const meeting = meetingsToImport[i];
      const meetingTitle = meeting.title || meeting.meeting_title || 'Untitled Meeting';
      setProgress({ current: i + 1, total: meetingsToImport.length, currentName: meetingTitle });

      try {
        const recordingId = meeting.recording_id;
        const transcriptResponse = await edgeFunctionService.callFunction('fathom-api', {
          action: 'get-transcript',
          userId,
          accountId,
          recordingId,
        });

        if (!transcriptResponse?.success) {
          throw new Error(transcriptResponse?.error || 'Failed to fetch transcript');
        }

        const externalDomains = getExternalDomains(meeting);

        if (onImportFathomSessions) {
          await onImportFathomSessions({
            name: meetingTitle,
            transcript: buildFathomTranscriptObject(transcriptResponse.transcript),
            source: 'fathom',
            sourceMetadata: {
              recording_id: recordingId,
              url: meeting.url,
              share_url: meeting.share_url,
              imported_at: new Date().toISOString(),
              duration_minutes: getMeetingDuration(meeting),
              scheduled_start: meeting.scheduled_start_time,
              scheduled_end: meeting.scheduled_end_time,
              recording_start: meeting.recording_start_time,
              recording_end: meeting.recording_end_time,
              created_at: meeting.created_at,
              calendar_invitees: meeting.calendar_invitees,
              recorded_by: meeting.recorded_by,
              external_domains: externalDomains,
            },
          });
        }

        results.allExternalDomains.push(...externalDomains);
        results.success++;
      } catch (err) {
        results.failed++;
        results.errors.push(`${meetingTitle}: ${err.message}`);
        console.error(`Failed to import meeting "${meetingTitle}":`, err);
      }
    }

    setImporting(false);

    if (results.failed > 0) {
      setError(
        `Imported ${results.success}/${meetingsToImport.length} meetings. ` +
        `${results.failed} failed: ${results.errors.join('; ')}`
      );
    } else {
      setError('');
      setOpen(false);
    }

    setSelected({});

    // Offer to link new attendee domains to the project's customer.
    if (results.success > 0 && results.allExternalDomains.length > 0 && projectCustomer && findCustomerByDomain) {
      const uniqueDomains = [...new Set(results.allExternalDomains.map((d) => d.toLowerCase()))];
      const customerDomains = (projectCustomer.domain || []).map((d) => d.toLowerCase());

      const reviewItems = uniqueDomains.map((domain) => {
        const isLinkedToThisCustomer = customerDomains.includes(domain);
        const matchedCustomer = findCustomerByDomain(domain);
        const isLinkedToOther = matchedCustomer && matchedCustomer.id !== projectCustomer.id;
        return {
          domain,
          status: isLinkedToThisCustomer ? 'linked' : isLinkedToOther ? 'other_customer' : 'unlinked',
          matchedCustomer: isLinkedToOther ? matchedCustomer : null,
          selected: !isLinkedToThisCustomer && !isLinkedToOther,
        };
      });

      if (reviewItems.some((r) => r.status !== 'linked')) {
        setDomainReviewData(reviewItems);
        setDomainReviewOpen(true);
      }
    }
  }, [userId, accountId, onImportFathomSessions, projectCustomer, findCustomerByDomain]);

  /**
   * Import the selected meetings; first confirm when attendee domains belong
   * to a different customer.
   */
  const importSelected = useCallback(() => {
    const selectedMeetings = meetings.filter((m) => selected[m.recording_id]);
    if (selectedMeetings.length === 0) return;

    if (projectCustomer && findCustomerByDomain) {
      const allDomains = new Set();
      selectedMeetings.forEach((meeting) => {
        (meeting.calendar_invitees || [])
          .filter((inv) => inv.is_external)
          .forEach((inv) => allDomains.add(inv.email_domain?.toLowerCase()));
      });

      const customerDomains = (projectCustomer.domain || []).map((d) => d.toLowerCase());
      const found = [];
      allDomains.forEach((domain) => {
        if (!domain || customerDomains.includes(domain)) return;
        const matchedCustomer = findCustomerByDomain(domain);
        if (matchedCustomer && matchedCustomer.id !== projectCustomer.id) {
          found.push({ domain, matchedCustomer });
        }
      });

      if (found.length > 0) {
        setConflicts(found);
        setPendingMeetings(selectedMeetings);
        setConflictOpen(true);
        return;
      }
    }

    executeImport(selectedMeetings);
  }, [meetings, selected, projectCustomer, findCustomerByDomain, executeImport]);

  const cancelConflict = useCallback(() => {
    setConflictOpen(false);
    setPendingMeetings([]);
    setConflicts([]);
  }, []);

  const confirmConflict = useCallback(() => executeImport(pendingMeetings), [executeImport, pendingMeetings]);

  const toggleDomainReview = useCallback((domain) => {
    setDomainReviewData((prev) =>
      prev.map((item) => (item.domain === domain ? { ...item, selected: !item.selected } : item))
    );
  }, []);

  const closeDomainReview = useCallback(() => {
    if (!domainReviewSaving) setDomainReviewOpen(false);
  }, [domainReviewSaving]);

  /** Add the selected unlinked domains to the project's customer. */
  const saveDomainReview = useCallback(async () => {
    if (!projectCustomer || !onUpdateCustomer) return;

    const domainsToAdd = domainReviewData
      .filter((item) => item.selected && item.status === 'unlinked')
      .map((item) => item.domain);

    if (domainsToAdd.length === 0) {
      setDomainReviewOpen(false);
      return;
    }

    setDomainReviewSaving(true);
    try {
      const existingDomains = projectCustomer.domain || [];
      await onUpdateCustomer(projectCustomer.id, { domain: [...new Set([...existingDomains, ...domainsToAdd])] });
      setDomainReviewOpen(false);
    } catch (err) {
      console.error('Error saving domains to customer:', err);
    } finally {
      setDomainReviewSaving(false);
    }
  }, [domainReviewData, projectCustomer, onUpdateCustomer]);

  return {
    hasIntegration,
    open,
    openImport,
    closeImport,
    meetings,
    loading,
    loadingMore,
    error,
    selected,
    selectedCount,
    allSelected,
    toggleSelected,
    toggleSelectAll,
    importing,
    progress,
    weeksBack,
    loadOlder,
    importSelected,
    conflictOpen,
    conflicts,
    pendingMeetings,
    cancelConflict,
    confirmConflict,
    domainReviewOpen,
    domainReviewData,
    domainReviewSaving,
    toggleDomainReview,
    closeDomainReview,
    saveDomainReview,
    projectCustomer,
  };
}
